import React, { useEffect, useState, useMemo } from 'react';
import {
  BarChart3, Download, Calendar, TrendingUp, Clock, ThumbsUp,
  Users, Building2, ShieldCheck, ChevronDown,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { format, subDays, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import api from '../lib/api';

// ── Palette for pie / bar charts ─────────────────────────────
const COLORS = ['#2563eb', '#16a34a', '#d97706', '#dc2626', '#7c3aed', '#0891b2', '#db2777', '#65a30d'];

// ── Date range presets ───────────────────────────────────────
const PRESETS = [
  { label: 'Last 7 days',   days: 7 },
  { label: 'Last 30 days',  days: 30 },
  { label: 'Last 90 days',  days: 90 },
  { label: 'This month',    fn: () => ({ start: startOfMonth(new Date()), end: new Date() }) },
  { label: 'Last month',    fn: () => ({ start: startOfMonth(subMonths(new Date(), 1)), end: endOfMonth(subMonths(new Date(), 1)) }) },
];

const TABS = [
  { key: 'overview',     label: 'Overview',     icon: TrendingUp },
  { key: 'team',         label: 'Team',         icon: Users },
  { key: 'time',         label: 'Time',         icon: Clock },
  { key: 'satisfaction', label: 'Satisfaction',  icon: ThumbsUp },
  { key: 'orgs',         label: 'Organizations', icon: Building2 },
  { key: 'sla',          label: 'SLA',          icon: ShieldCheck },
];

function fmt(d) { return format(d, 'yyyy-MM-dd'); }
function fmtShort(dateStr) {
  try { return format(new Date(dateStr + 'T00:00:00'), 'MMM d'); } catch { return dateStr; }
}

// ── Custom tooltip ───────────────────────────────────────────
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8,
      padding: '10px 14px', fontSize: 12.5, boxShadow: 'var(--shadow)',
    }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{fmtShort(label) || label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color, flexShrink: 0 }} />
          <span style={{ color: 'var(--text-secondary)' }}>{p.name}:</span>
          <span style={{ fontWeight: 600 }}>{p.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function ReportsPage() {
  const [tab, setTab] = useState('overview');
  const [range, setRange] = useState({ start: subDays(new Date(), 30), end: new Date() });
  const [presetOpen, setPresetOpen] = useState(false);
  const [groupBy, setGroupBy] = useState('day');

  // Data
  const [overview, setOverview] = useState(null);
  const [volume, setVolume] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [techs, setTechs] = useState(null);
  const [satisfaction, setSatisfaction] = useState(null);
  const [time, setTime] = useState(null);
  const [orgs, setOrgs] = useState(null);
  const [sla, setSla] = useState(null);
  const [loading, setLoading] = useState(true);

  const qs = useMemo(() => `start=${fmt(range.start)}&end=${fmt(range.end)}`, [range]);

  // Fetch data for current tab
  useEffect(() => {
    setLoading(true);
    const fetches = [];

    if (tab === 'overview') {
      fetches.push(
        api.get(`/reports/overview?${qs}`).then(d => setOverview(d.overview)),
        api.get(`/reports/ticket-volume?${qs}&group=${groupBy}`).then(d => setVolume(d.volume)),
        api.get(`/reports/ticket-breakdown?${qs}`).then(d => setBreakdown(d.breakdown)),
      );
    } else if (tab === 'team') {
      fetches.push(api.get(`/reports/tech-performance?${qs}`).then(d => setTechs(d.techs)));
    } else if (tab === 'satisfaction') {
      fetches.push(api.get(`/reports/satisfaction?${qs}`).then(d => setSatisfaction(d.satisfaction)));
    } else if (tab === 'time') {
      fetches.push(api.get(`/reports/time-tracking?${qs}`).then(d => setTime(d.time)));
    } else if (tab === 'orgs') {
      fetches.push(api.get(`/reports/organizations?${qs}`).then(d => setOrgs(d.organizations)));
    } else if (tab === 'sla') {
      fetches.push(api.get(`/reports/sla?${qs}`).then(d => setSla(d.sla)));
    }

    Promise.allSettled(fetches).finally(() => setLoading(false));
  }, [tab, qs, groupBy]);

  // Merge volume data for chart
  const volumeData = useMemo(() => {
    if (!volume) return [];
    const map = {};
    (volume.created || []).forEach(r => { map[r.period] = { period: r.period, created: r.count, resolved: 0 }; });
    (volume.resolved || []).forEach(r => {
      if (map[r.period]) map[r.period].resolved = r.count;
      else map[r.period] = { period: r.period, created: 0, resolved: r.count };
    });
    return Object.values(map).sort((a, b) => (a.period > b.period ? 1 : -1));
  }, [volume]);

  const applyPreset = (p) => {
    if (p.fn) {
      const r = p.fn();
      setRange(r);
    } else {
      setRange({ start: subDays(new Date(), p.days), end: new Date() });
    }
    setPresetOpen(false);
  };

  const exportCsv = (endpoint) => {
    const url = `${api.defaults?.baseURL || '/api'}/reports/${endpoint}?${qs}&format=csv`;
    window.open(url, '_blank');
  };

  const Loader = () => (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
      <div className="spinner" style={{ width: 24, height: 24 }} />
    </div>
  );

  // ── OVERVIEW TAB ─────────────────────────────────────────────
  const renderOverview = () => {
    if (loading || !overview) return <Loader />;

    const cards = [
      { label: 'Tickets Created', value: overview.created, color: '#2563eb' },
      { label: 'Tickets Resolved', value: overview.resolved, color: '#16a34a' },
      { label: 'Avg Resolution', value: overview.avg_resolution_hours ? `${overview.avg_resolution_hours}h` : '—', color: '#d97706' },
      { label: 'Satisfaction', value: overview.satisfaction_pct !== null ? `${overview.satisfaction_pct}%` : '—', color: '#7c3aed' },
      { label: 'Hours Logged', value: overview.total_hours, color: '#0891b2' },
    ];

    return (
      <>
        {/* Stat cards */}
        <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))' }}>
          {cards.map(c => (
            <div key={c.label} className="stat-card">
              <div className="stat-label" style={{ color: c.color }}>{c.label}</div>
              <div className="stat-value" style={{ color: c.color }}>{c.value}</div>
            </div>
          ))}
        </div>

        {/* Volume chart */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="flex justify-between items-center" style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: 14 }}>Ticket Volume</h3>
            <div className="flex gap-2 items-center">
              <select className="form-select" style={{ width: 'auto', padding: '4px 8px', fontSize: 12 }}
                value={groupBy} onChange={e => setGroupBy(e.target.value)}>
                <option value="day">Daily</option>
                <option value="week">Weekly</option>
                <option value="month">Monthly</option>
              </select>
              <button className="btn btn-ghost btn-sm" onClick={() => exportCsv('ticket-volume')} title="Export CSV">
                <Download size={13} />
              </button>
            </div>
          </div>
          {volumeData.length === 0 ? (
            <div className="text-muted text-sm" style={{ textAlign: 'center', padding: 40 }}>No ticket data for this period</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={volumeData} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="period" tickFormatter={fmtShort} tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="created" name="Created" fill="#2563eb" radius={[3, 3, 0, 0]} />
                <Bar dataKey="resolved" name="Resolved" fill="#16a34a" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Breakdown charts */}
        {breakdown && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
            {[
              { title: 'By Status', data: breakdown.by_status },
              { title: 'By Priority', data: breakdown.by_priority },
              { title: 'By Category', data: breakdown.by_category },
            ].map(({ title, data }) => (
              <div key={title} className="card">
                <h3 style={{ fontSize: 14, marginBottom: 16 }}>{title}</h3>
                {!data?.length ? (
                  <div className="text-muted text-sm" style={{ textAlign: 'center', padding: 24 }}>No data</div>
                ) : (
                  <>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie data={data} dataKey="count" nameKey="name" cx="50%" cy="50%"
                          innerRadius={50} outerRadius={80} paddingAngle={2}>
                          {data.map((entry, i) => (
                            <Cell key={i} fill={entry.color || COLORS[i % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginTop: 8, justifyContent: 'center' }}>
                      {data.map((d, i) => (
                        <div key={i} className="flex items-center gap-2" style={{ fontSize: 12 }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: d.color || COLORS[i % COLORS.length], flexShrink: 0 }} />
                          <span className="text-secondary">{d.name}</span>
                          <span className="font-semibold">{d.count}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </>
    );
  };

  // ── TEAM TAB ─────────────────────────────────────────────────
  const renderTeam = () => {
    if (loading || !techs) return <Loader />;
    return (
      <div className="card" style={{ padding: 0 }}>
        <div className="flex justify-between items-center" style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ fontSize: 14 }}>Technician Performance</h3>
          <button className="btn btn-ghost btn-sm" onClick={() => exportCsv('tech-performance')}>
            <Download size={13} /> CSV
          </button>
        </div>
        {techs.length === 0 ? (
          <div className="empty-state" style={{ padding: 48 }}>
            <Users size={28} />
            <p>No technician data for this period</p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Technician</th>
                  <th style={{ textAlign: 'right' }}>Assigned</th>
                  <th style={{ textAlign: 'right' }}>Resolved</th>
                  <th style={{ textAlign: 'right' }}>Avg Resolution</th>
                  <th style={{ textAlign: 'right' }}>Hours Logged</th>
                </tr>
              </thead>
              <tbody>
                {techs.map(t => (
                  <tr key={t.id}>
                    <td className="font-medium">{t.full_name}</td>
                    <td style={{ textAlign: 'right' }}>{t.assigned_count}</td>
                    <td style={{ textAlign: 'right' }}>{t.resolved_count}</td>
                    <td style={{ textAlign: 'right' }}>{t.avg_resolution_hours ? `${t.avg_resolution_hours}h` : '—'}</td>
                    <td style={{ textAlign: 'right' }}>{t.total_hours}h</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ── SATISFACTION TAB ─────────────────────────────────────────
  const renderSatisfaction = () => {
    if (loading || !satisfaction) return <Loader />;
    const { over_time, by_tech } = satisfaction;

    return (
      <>
        {/* Over time chart */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="flex justify-between items-center" style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: 14 }}>Satisfaction Over Time</h3>
            <button className="btn btn-ghost btn-sm" onClick={() => exportCsv('satisfaction')}>
              <Download size={13} /> CSV
            </button>
          </div>
          {!over_time?.length ? (
            <div className="text-muted text-sm" style={{ textAlign: 'center', padding: 40 }}>No ratings for this period</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={over_time} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="period" tickFormatter={fmtShort} tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="positive" name="Positive" stroke="#16a34a" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="negative" name="Negative" stroke="#dc2626" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* By tech */}
        {by_tech?.length > 0 && (
          <div className="card" style={{ padding: 0 }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
              <h3 style={{ fontSize: 14 }}>Satisfaction by Technician</h3>
            </div>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Technician</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                    <th style={{ textAlign: 'right' }}>Positive</th>
                    <th style={{ textAlign: 'right' }}>Negative</th>
                    <th style={{ textAlign: 'right' }}>Satisfaction</th>
                  </tr>
                </thead>
                <tbody>
                  {by_tech.map((t, i) => {
                    const pct = t.total > 0 ? Math.round((t.positive / t.total) * 100) : 0;
                    return (
                      <tr key={i}>
                        <td className="font-medium">{t.full_name}</td>
                        <td style={{ textAlign: 'right' }}>{t.total}</td>
                        <td style={{ textAlign: 'right', color: '#16a34a' }}>{t.positive}</td>
                        <td style={{ textAlign: 'right', color: '#dc2626' }}>{t.negative}</td>
                        <td style={{ textAlign: 'right' }}>
                          <span style={{
                            display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                            background: pct >= 80 ? 'var(--green-light)' : pct >= 50 ? 'var(--amber-light)' : 'var(--red-light)',
                            color: pct >= 80 ? 'var(--green)' : pct >= 50 ? 'var(--amber)' : 'var(--red)',
                          }}>{pct}%</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </>
    );
  };

  // ── TIME TAB ─────────────────────────────────────────────────
  const renderTime = () => {
    if (loading || !time) return <Loader />;
    const { by_tech, by_org, by_project } = time;

    const renderBar = (data, nameKey, title) => {
      if (!data?.length) return null;
      return (
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 14, marginBottom: 16 }}>{title}</h3>
          <ResponsiveContainer width="100%" height={Math.max(160, data.length * 40)}>
            <BarChart data={data} layout="vertical" margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} unit="h" />
              <YAxis type="category" dataKey={nameKey} width={140} tick={{ fontSize: 12, fill: 'var(--text-secondary)' }} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="total_hours" name="Hours" fill="#0891b2" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      );
    };

    return (
      <>
        <div className="flex justify-between items-center" style={{ marginBottom: 16 }}>
          <div />
          <button className="btn btn-ghost btn-sm" onClick={() => exportCsv('time-tracking')}>
            <Download size={13} /> Export CSV
          </button>
        </div>
        {renderBar(by_tech, 'full_name', 'Hours by Technician')}
        {renderBar(by_org, 'name', 'Hours by Organization')}
        {renderBar(by_project, 'title', 'Hours by Project')}
        {!by_tech?.length && !by_org?.length && !by_project?.length && (
          <div className="card">
            <div className="empty-state" style={{ padding: 48 }}>
              <Clock size={28} />
              <p>No time entries for this period</p>
            </div>
          </div>
        )}
      </>
    );
  };

  // ── ORGS TAB ─────────────────────────────────────────────────
  const renderOrgs = () => {
    if (loading || !orgs) return <Loader />;
    return (
      <div className="card" style={{ padding: 0 }}>
        <div className="flex justify-between items-center" style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ fontSize: 14 }}>Organization Activity</h3>
          <button className="btn btn-ghost btn-sm" onClick={() => exportCsv('organizations')}>
            <Download size={13} /> CSV
          </button>
        </div>
        {orgs.length === 0 ? (
          <div className="empty-state" style={{ padding: 48 }}>
            <Building2 size={28} />
            <p>No organization data for this period</p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Organization</th>
                  <th style={{ textAlign: 'right' }}>Tickets</th>
                  <th style={{ textAlign: 'right' }}>Resolved</th>
                  <th style={{ textAlign: 'right' }}>Avg Resolution</th>
                  <th style={{ textAlign: 'right' }}>Hours</th>
                </tr>
              </thead>
              <tbody>
                {orgs.map(o => (
                  <tr key={o.id}>
                    <td className="font-medium">{o.name}</td>
                    <td style={{ textAlign: 'right' }}>{o.ticket_count}</td>
                    <td style={{ textAlign: 'right' }}>{o.resolved_count}</td>
                    <td style={{ textAlign: 'right' }}>{o.avg_resolution_hours ? `${o.avg_resolution_hours}h` : '—'}</td>
                    <td style={{ textAlign: 'right' }}>{o.total_hours}h</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ── SLA TAB ──────────────────────────────────────────────────
  const renderSla = () => {
    if (loading || !sla) return <Loader />;
    const { by_priority, open_breaches } = sla;

    return (
      <>
        {/* Open breach summary */}
        {open_breaches && (
          <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', marginBottom: 16 }}>
            <div className="stat-card">
              <div className="stat-label">Open Tickets</div>
              <div className="stat-value">{open_breaches.open_total}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label" style={{ color: '#dc2626' }}>Response Breached</div>
              <div className="stat-value" style={{ color: '#dc2626' }}>{open_breaches.response_breached}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label" style={{ color: '#d97706' }}>Resolution Breached</div>
              <div className="stat-value" style={{ color: '#d97706' }}>{open_breaches.resolution_breached}</div>
            </div>
          </div>
        )}

        {/* By priority */}
        <div className="card" style={{ padding: 0 }}>
          <div className="flex justify-between items-center" style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
            <h3 style={{ fontSize: 14 }}>SLA Compliance by Priority</h3>
            <button className="btn btn-ghost btn-sm" onClick={() => exportCsv('sla')}>
              <Download size={13} /> CSV
            </button>
          </div>
          {!by_priority?.length ? (
            <div className="empty-state" style={{ padding: 48 }}>
              <ShieldCheck size={28} />
              <p>No resolved tickets with SLA data</p>
            </div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Priority</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                    <th style={{ textAlign: 'right' }}>Met</th>
                    <th style={{ textAlign: 'right' }}>Breached</th>
                    <th style={{ textAlign: 'right' }}>Compliance</th>
                  </tr>
                </thead>
                <tbody>
                  {by_priority.map((r, i) => {
                    const pct = r.total > 0 ? Math.round((r.met / r.total) * 100) : 0;
                    return (
                      <tr key={i}>
                        <td>
                          <span className="flex items-center gap-2">
                            {r.color && <span style={{ width: 8, height: 8, borderRadius: '50%', background: r.color, flexShrink: 0 }} />}
                            <span className="font-medium">{r.priority_name}</span>
                          </span>
                        </td>
                        <td style={{ textAlign: 'right' }}>{r.total}</td>
                        <td style={{ textAlign: 'right', color: '#16a34a' }}>{r.met}</td>
                        <td style={{ textAlign: 'right', color: '#dc2626' }}>{r.breached}</td>
                        <td style={{ textAlign: 'right' }}>
                          <span style={{
                            display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                            background: pct >= 90 ? 'var(--green-light)' : pct >= 70 ? 'var(--amber-light)' : 'var(--red-light)',
                            color: pct >= 90 ? 'var(--green)' : pct >= 70 ? 'var(--amber)' : 'var(--red)',
                          }}>{pct}%</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </>
    );
  };

  // ── RENDER ───────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div className="page-header">
        <div className="flex items-center gap-3">
          <h2><BarChart3 size={16} style={{ display: 'inline', verticalAlign: -2, marginRight: 6, opacity: 0.5 }} />Reports</h2>
        </div>
        <div className="flex items-center gap-2">
          {/* Date range */}
          <div style={{ position: 'relative' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setPresetOpen(!presetOpen)}>
              <Calendar size={13} />
              {format(range.start, 'MMM d')} – {format(range.end, 'MMM d, yyyy')}
              <ChevronDown size={12} />
            </button>
            {presetOpen && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 49 }} onClick={() => setPresetOpen(false)} />
                <div style={{
                  position: 'absolute', right: 0, top: '100%', marginTop: 4, zIndex: 50,
                  background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                  boxShadow: 'var(--shadow-lg)', minWidth: 200, padding: 4,
                }}>
                  {PRESETS.map(p => (
                    <button key={p.label} className="btn btn-ghost btn-sm w-full"
                      style={{ justifyContent: 'flex-start', borderRadius: 'var(--radius-sm)' }}
                      onClick={() => applyPreset(p)}>
                      {p.label}
                    </button>
                  ))}
                  <hr className="divider" style={{ margin: '4px 0' }} />
                  <div style={{ padding: '8px 10px', display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type="date" className="form-input" style={{ padding: '4px 6px', fontSize: 12, width: 'auto' }}
                      value={fmt(range.start)}
                      onChange={e => { if (e.target.value) setRange(r => ({ ...r, start: new Date(e.target.value + 'T00:00:00') })); }} />
                    <span className="text-muted">–</span>
                    <input type="date" className="form-input" style={{ padding: '4px 6px', fontSize: 12, width: 'auto' }}
                      value={fmt(range.end)}
                      onChange={e => { if (e.target.value) setRange(r => ({ ...r, end: new Date(e.target.value + 'T00:00:00') })); }} />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="page-body">
        {/* Mobile tab select */}
        <select className="tab-mobile-select form-select" value={tab} onChange={e => setTab(e.target.value)}>
          {TABS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        {/* Tabs */}
        <div className="report-tabs tab-bar" style={{
          display: 'flex', gap: 2, marginBottom: 20, borderBottom: '1px solid var(--border)', paddingBottom: 0,
          overflowX: 'auto', flexShrink: 0,
        }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', fontSize: 13, fontWeight: tab === t.key ? 600 : 400,
              color: tab === t.key ? 'var(--accent)' : 'var(--text-secondary)',
              borderBottom: tab === t.key ? '2px solid var(--accent)' : '2px solid transparent',
              background: 'none', border: 'none', borderBottomStyle: 'solid', borderBottomWidth: 2,
              cursor: 'pointer', whiteSpace: 'nowrap', transition: 'color 0.15s',
              marginBottom: -1,
            }}>
              <t.icon size={14} />{t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {tab === 'overview' && renderOverview()}
        {tab === 'team' && renderTeam()}
        {tab === 'satisfaction' && renderSatisfaction()}
        {tab === 'time' && renderTime()}
        {tab === 'orgs' && renderOrgs()}
        {tab === 'sla' && renderSla()}
      </div>
    </div>
  );
}
